import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../components/AuthContext';
import { passwordRedirectUrl } from '../lib/appUrl';
import { landingChoicesFor, landingPageFor, pageRuleAuth } from '../lib/deepLink';
import { barForRole, resolveBar, BAR_SLOTS, BAR_DESTINATION_CAP } from '../lib/bottomBar';
import { canSeePurchasingNav, canSeeInsights, canSeeInvoicingNav, canSeeWelding, canRecordWeldsHere } from '../lib/permissions';
import { IconGripVertical, IconChevronUp, IconChevronDown } from '@tabler/icons-react';
import { DESKTOP_COLUMN, MOBILE_BREAKPOINT } from '../lib/layout';
import { IconCheck, IconChevronRight, IconDeviceDesktop, IconDeviceMobile, IconLock } from '@tabler/icons-react';

const ROLE_LABELS = {
  admin: 'Admin',
  manager: 'Branch manager',
  team_member: 'Team member',
  executive: 'Executive',
  purchasing: 'Purchasing',
};

// #167, Session 78j. The gate's Screen 1.
//
// This page rendered `<div style={{ padding: 32 }}>My profile — coming soon</div>`
// from Session 67 until now -- fourteen months of "coming soon" in a menu every
// user can reach.
//
// What it carries and why, gate decision D8: a profile page holding only two
// navigation settings reads as a settings page mislabelled. Name, email, role,
// branch and a password change belong here. Role and branch are deliberately
// read-only (callout A) -- they are the two fields that decide what a person can
// see and approve, and a profile page is exactly where someone would expect to
// edit them, which is why they must visibly refuse. Omitting them would be
// worse: someone who cannot see their own branch cannot tell you it is wrong.
export default function MyProfilePage({ landingDefaults }) {
  const auth = useAuth();
  const { profile, retryProfile } = auth;
  // #746, Session 79.22b.6.3 -- the page rule's input from its one helper
  // (deepLink.js, pageRuleAuth()), never a hand-built copy: Welding's rule reads
  // the person's branch, so a copy of the five role flags would never offer it.
  const ruleAuth = pageRuleAuth(auth);

  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [branchName, setBranchName] = useState('');
  const [picker, setPicker] = useState(null); // 'desktop' | 'mobile' | null
  const [saving, setSaving] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');

  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth < MOBILE_BREAKPOINT); }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Read rather than hardcode. CLAUDE.md records the four-branch list being
  // hardcoded independently in 13 files as #252, and this would have been the
  // fourteenth for one label.
  const loadBranch = useCallback(async () => {
    if (!profile?.branch_id) return;
    const { data } = await supabase
      .from('branches')
      .select('name')
      .eq('id', profile.branch_id)
      .maybeSingle();
    if (data?.name) setBranchName(data.name);
  }, [profile?.branch_id]);

  useEffect(() => { loadBranch(); }, [loadBranch]);

  const choices = landingChoicesFor(ruleAuth);

  // Callout D — the role default is shown, not hidden behind the override.
  // Someone who has changed it can see what they changed; someone who has not
  // can see that a decision was made on their behalf rather than by accident.
  function roleDefaultNameFor(platformMobile) {
    const key = landingPageFor(ruleAuth, {
      profile: { ...profile, landing_page_desktop: null, landing_page_mobile: null },
      roleDefaults: landingDefaults,
      isMobile: platformMobile,
    });
    return nameFor(key);
  }

  function nameFor(key) {
    return choices.find(c => c.key === key)?.name
      || LANDING_FALLBACK_NAMES[key]
      || 'Fleet Inventory';
  }

  function currentFor(platformMobile) {
    const chosen = platformMobile ? profile?.landing_page_mobile : profile?.landing_page_desktop;
    return chosen ? nameFor(chosen) : null;
  }

  async function chooseLanding(platformMobile, pageKey) {
    if (!profile?.id) return;
    setSaving(true);
    const column = platformMobile ? 'landing_page_mobile' : 'landing_page_desktop';
    const { error } = await supabase
      .from('users')
      .update({ [column]: pageKey })
      .eq('id', profile.id);
    setSaving(false);
    setPicker(null);
    // retryProfile() re-fetches the profile row. Its name is about the error
    // path it was written for, but it is the one function AuthContext exposes
    // that reloads this data, and duplicating the fetch here would give the app
    // two copies of the profile-shaped truth.
    if (!error) retryProfile();
  }

  async function clearLanding(platformMobile) {
    await chooseLanding(platformMobile, null);
  }

  // Callout B — a reset link rather than an inline password field. It reuses the
  // path #263 made origin-correct, so a link issued from staging redeems against
  // staging. An inline field would be a second credential surface to get right,
  // and there is no reason to own two.
  async function sendPasswordReset() {
    setResetError('');
    const { error } = await supabase.auth.resetPasswordForEmail(profile?.email, {
      redirectTo: passwordRedirectUrl(),
    });
    if (error) { setResetError(error.message); return; }
    setResetSent(true);
  }

  const column = { maxWidth: isMobile ? 'none' : DESKTOP_COLUMN, margin: '0 auto', width: '100%', boxSizing: 'border-box' };
  const card = { background: 'white', border: '1px solid #E8E8E8', borderRadius: 12, overflow: 'hidden' };
  const rowStyle = { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: '1px solid #F2F2F0' };
  const labelStyle = { fontSize: 13, fontWeight: 600, color: '#0F0F0F' };
  const hintStyle = { fontSize: 11, color: '#9A9A9A', marginTop: 2 };
  const valueStyle = { fontSize: 13, color: '#5C5C5C', textAlign: 'right', marginLeft: 'auto' };
  const sectionLabel = { fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '0.8px', margin: '18px 0 8px' };

  const roleLabel = ROLE_LABELS[profile?.role] || 'Team member';

  return (
    <div style={{ background: '#F5F5F3', minHeight: '100vh', paddingBottom: 90 }}>

      <div style={{ background: 'white', padding: '14px 16px 12px', borderBottom: '1px solid #E8E8E8' }}>
        <div style={column}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#0F0F0F' }}>My profile</div>
          <div style={{ fontSize: 12, color: '#7A7A7A', marginTop: 3 }}>
            {profile?.full_name}{branchName ? ` · ${branchName}` : ''} · {roleLabel}
          </div>
        </div>
      </div>

      <div style={{ ...column, padding: '0 14px' }}>

        <div style={sectionLabel}>Account</div>
        <div style={card}>
          <div style={rowStyle}>
            <div style={{ minWidth: 0 }}>
              <div style={labelStyle}>Full name</div>
              <div style={hintStyle}>Shown on receipts, approvals and audit entries</div>
            </div>
            <div style={valueStyle}>{profile?.full_name || '—'}</div>
          </div>
          <div style={rowStyle}>
            <div style={{ minWidth: 0 }}>
              <div style={labelStyle}>Email</div>
              <div style={hintStyle}>Used to sign in and to receive notifications</div>
            </div>
            <div style={{ ...valueStyle, overflow: 'hidden', textOverflow: 'ellipsis' }}>{profile?.email || '—'}</div>
          </div>
          {/* Callout A. "Set by an administrator" is a permission boundary
              rendered as copy, on the two fields that decide what a person can
              see and approve. */}
          <div style={rowStyle}>
            <div style={{ minWidth: 0 }}>
              <div style={labelStyle}>Role</div>
              <div style={hintStyle}>Set by an administrator</div>
            </div>
            <div style={{ ...valueStyle, display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end' }}>
              <IconLock size={13} color="#C0C0C0" />{roleLabel}
            </div>
          </div>
          <div style={rowStyle}>
            <div style={{ minWidth: 0 }}>
              <div style={labelStyle}>Branch</div>
              <div style={hintStyle}>Set by an administrator</div>
            </div>
            <div style={{ ...valueStyle, display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end' }}>
              <IconLock size={13} color="#C0C0C0" />{branchName || '—'}
            </div>
          </div>
          <div style={{ ...rowStyle, borderBottom: 'none' }}>
            <div style={{ minWidth: 0 }}>
              <div style={labelStyle}>Password</div>
              <div style={hintStyle}>
                {resetSent
                  ? 'Reset link sent — check your email'
                  : resetError || 'Sends a reset link to your email'}
              </div>
            </div>
            <button
              onClick={sendPasswordReset}
              disabled={resetSent}
              style={{ marginLeft: 'auto', background: 'white', border: '1px solid #E0E0E0', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 700, color: resetSent ? '#9A9A9A' : '#0F0F0F', cursor: resetSent ? 'default' : 'pointer' }}
            >
              {resetSent ? 'Sent' : 'Change'}
            </button>
          </div>
        </div>

        <div style={sectionLabel}>Where Stede opens</div>
        <div style={card}>
          {/* D3 — stored per platform. The same person wants Invoicing at a desk
              and Stock Home on a phone; one value would be wrong on one of the
              two devices every time. */}
          {[false, true].map(platformMobile => {
            const current = currentFor(platformMobile);
            const Icon = platformMobile ? IconDeviceMobile : IconDeviceDesktop;
            return (
              <div key={platformMobile ? 'mobile' : 'desktop'} style={{ ...rowStyle, borderBottom: platformMobile ? 'none' : '1px solid #F2F2F0' }}>
                <Icon size={20} color="#7A7A7A" />
                <div style={{ minWidth: 0 }}>
                  <div style={labelStyle}>{platformMobile ? 'On your phone' : 'On this computer'}</div>
                  <div style={hintStyle}>Default for your role: {roleDefaultNameFor(platformMobile)}</div>
                </div>
                <button
                  onClick={() => setPicker(platformMobile ? 'mobile' : 'desktop')}
                  style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, background: 'white', border: '1px solid #E0E0E0', borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 700, color: current ? '#0F0F0F' : '#7A7A7A', cursor: 'pointer' }}
                >
                  {current || `${roleDefaultNameFor(platformMobile)} · default`}
                  <IconChevronRight size={14} color="#C0C0C0" />
                </button>
              </div>
            );
          })}
        </div>

        {/* D10 — no setting is required, and every one has a working default.
            The personal layer is a bonus, never the mechanism (callout G). */}
        <div style={{ fontSize: 11, color: '#9A9A9A', marginTop: 8, lineHeight: 1.45 }}>
          You do not have to choose. Leave either one on its default and Stede opens
          where your role opens.
        </div>
      </div>

      <BottomBarOrder profile={profile} roleFlags={ruleAuth} retryProfile={retryProfile} isMobile={isMobile} />

      {picker && (
        <div
          onClick={() => setPicker(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 24 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'white', borderRadius: isMobile ? '16px 16px 0 0' : 14, width: '100%', maxWidth: isMobile ? 'none' : 420, maxHeight: '80vh', overflowY: 'auto' }}
          >
            <div style={{ padding: '16px 18px 10px' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0F0F0F' }}>
                {picker === 'mobile' ? 'Open Stede on your phone' : 'Open Stede on this computer'}
              </div>
              <div style={{ fontSize: 11, color: '#9A9A9A', marginTop: 3 }}>
                {picker === 'mobile' ? 'Your computer is set separately.' : 'Your phone is set separately.'}
              </div>
            </div>

            <button
              onClick={() => clearLanding(picker === 'mobile')}
              style={{ width: '100%', textAlign: 'left', padding: '12px 18px', border: 'none', borderTop: '1px solid #F2F2F0', background: 'white', cursor: 'pointer' }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F' }}>
                Use my role's default
              </div>
              <div style={{ fontSize: 11, color: '#9A9A9A', marginTop: 2 }}>
                {roleDefaultNameFor(picker === 'mobile')}
              </div>
            </button>

            {/* Callout C — only pages this role can actually reach. */}
            {choices.map(c => {
              const chosen = (picker === 'mobile' ? profile?.landing_page_mobile : profile?.landing_page_desktop) === c.key;
              return (
                <button
                  key={c.key}
                  onClick={() => chooseLanding(picker === 'mobile', c.key)}
                  disabled={saving}
                  style={{ width: '100%', textAlign: 'left', padding: '12px 18px', border: 'none', borderTop: '1px solid #F2F2F0', background: chosen ? '#F0FFF4' : 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F' }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: '#9A9A9A', marginTop: 2 }}>{c.blurb}</div>
                  </div>
                  {chosen && <IconCheck size={16} color="#1D9E75" />}
                </button>
              );
            })}

            <button
              onClick={() => setPicker(null)}
              style={{ width: '100%', padding: '14px', border: 'none', borderTop: '1px solid #E8E8E8', background: 'white', fontSize: 13, fontWeight: 700, color: '#7A7A7A', cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Only reached when a stored landing page is no longer in this role's choices --
// an administrator changed the role after the choice was made. The picker cannot
// offer these, but the row still has to name what is stored.
const LANDING_FALLBACK_NAMES = {
  fleet: 'Fleet Inventory',
  inv_home: 'Stock Home',
  requests_inbox: 'Requests inbox',
  purchasing_queue: 'Purchasing Queue',
  purchasing_invoicing: 'Invoicing',
  insights: 'Insights',
  inv_catalog: 'Catalog',
  inv_ledger: 'Ledger',
  // #746, Session 79.22b.6.3 -- the one opening page a person can lose by
  // MOVING BRANCH rather than by a role change: a welder moved to Dallas keeps
  // 'welding' stored, and this row still has to name it.
  welding: 'Welding',
};


// -- D4: the bottom bar, rearranged by the person who uses it -----------------
//
// #167 D4/D5, Session 78l. The second layer of the two-layer model: an
// administrator sets each role's bar in Company settings, and this is where an
// individual reorders their own copy of it.
//
// DRAG, not long-press, and that was decided by research rather than taste.
// ServiceTitan's Field Mobile App and Zoho CRM's iOS app both ship exactly this
// capability and both deliver it as drag-inside-a-settings-screen. No researched
// system uses long-press for navigation configuration -- long-press carries no
// affordance, is undiscoverable, and is the gesture most easily fired by
// accident with gloves on. Part 3 of this issue's original scope was dropped on
// that finding rather than deferred.
//
// REORDER ONLY, not compose. WHAT is on a person's bar is their role's business
// -- it is what an administrator is accountable for and what the role cards
// describe. What ORDER it sits in is the person's business, because that is
// thumb reach and nothing else. Letting someone remove Stock from their own bar
// four days before go-live would be a support call, not a feature.
//
// Scan is not in this list. D7 makes it the immovable centre: it is the only
// slot that is an action rather than a destination, the one-tap entry to the QR
// path #320 exists to protect, and the anchor the two-taps-under-three-seconds
// standard is measured from. Showing it as a locked row would invite dragging
// it; showing the finished bar underneath says the same thing without the tease.
//
// Pointer events rather than HTML5 drag-and-drop, deliberately. HTML5 DnD does
// not fire on touch at all, and mobile is this product's priority surface -- a
// desktop-only reorder on a mobile-first feature is the wrong half.
function BottomBarOrder({ profile, roleFlags, retryProfile, isMobile }) {
  const caps = {
    canSeePurchasing: canSeePurchasingNav(roleFlags),
    canSeeInsights: canSeeInsights(roleFlags),
    canSeeInvoicing: canSeeInvoicingNav({ ...roleFlags, profile }),
    // #746 -- as the bar itself passes it (Sidebar.js, BottomNav): the screen
    // that reorders the bar must offer exactly the slots the phone shows.
    canSeeWelding: canSeeWelding(roleFlags),
    canRecordWeldsHere: canRecordWeldsHere(roleFlags),
  };

  const roleBar = barForRole(profile?.role, caps);
  const roleOrder = [...roleBar.left, ...roleBar.right];

  // A stored bar is filtered against the role's own destinations rather than
  // trusted. Two things change underneath it: an administrator can change the
  // role's bar, and a person's role can change. Either leaves a stored key that
  // no longer belongs, and rendering it would put a row on this screen that the
  // reachability filter then drops -- so the settings screen would show five
  // slots and the phone would show four.
  //
  // #746, Session 79.22b.6.3 -- read through resolveBar(), the phone's own rule,
  // instead of a second copy of it here. The copy that stood here would have
  // thrown away every arrangement made before Welding reached a welder's bar,
  // while the phone kept it (746 D24): the two must agree or a saved
  // arrangement shows one way here and another on the phone.
  const hasStored = !!(profile?.bottom_bar && Array.isArray(profile.bottom_bar.left) && Array.isArray(profile.bottom_bar.right));
  const resolved = resolveBar(profile, caps);
  const usable = hasStored ? [...resolved.left, ...resolved.right] : null;

  const [order, setOrder] = useState(usable || roleOrder);
  const [dragIdx, setDragIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const startY = React.useRef(0);
  const liveOrder = React.useRef(order);

  // Re-sync when the profile reloads underneath us -- retryProfile() after a
  // save returns a new row, and an order held in state would otherwise outlive
  // an administrator's change to the role default.
  useEffect(() => {
    const next = usable || roleOrder;
    if (next.join() !== liveOrder.current.join()) {
      setOrder(next);
      liveOrder.current = next;
    }
  }, [profile?.bottom_bar, profile?.role, usable, roleOrder]);

  const ROW_H = 52;
  const isCustom = !!usable && usable.join() !== roleOrder.join();

  async function persist(next) {
    if (!profile?.id) return;
    // Split back into the two sides Scan sits between. The left side takes the
    // extra slot on an odd count, because slot one is the one a thumb reaches
    // without moving and the far right is the one it reaches last.
    const split = Math.ceil(next.length / 2);
    const payload = { left: next.slice(0, split), right: next.slice(split) };
    setSaving(true);
    setError('');
    const { error: updErr } = await supabase
      .from('users')
      .update({ bottom_bar: payload })
      .eq('id', profile.id);
    setSaving(false);
    if (updErr) { setError('Could not save that. ' + updErr.message); return; }
    retryProfile();
  }

  async function resetToRole() {
    if (!profile?.id) return;
    setSaving(true);
    setError('');
    const { error: updErr } = await supabase
      .from('users')
      .update({ bottom_bar: null })
      .eq('id', profile.id);
    setSaving(false);
    if (updErr) { setError('Could not save that. ' + updErr.message); return; }
    setOrder(roleOrder);
    liveOrder.current = roleOrder;
    retryProfile();
  }

  function handlePointerDown(e, i) {
    // Capture keeps the move events coming to this row even when the finger
    // travels off it, which it always does -- the row you grabbed is the row
    // you are dragging away from. Guarded because setPointerCapture throws
    // NotFoundError for a pointer id the browser does not consider active, and
    // an exception here would leave the list frozen rather than merely
    // uncaptured. Without capture the drag still works while the finger stays
    // inside the list, which is the degraded behaviour worth having.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* not fatal */ }
    startY.current = e.clientY;
    setDragIdx(i);
  }

  function handlePointerMove(e) {
    if (dragIdx === null) return;
    const shift = Math.round((e.clientY - startY.current) / ROW_H);
    if (!shift) return;
    const target = Math.min(order.length - 1, Math.max(0, dragIdx + shift));
    if (target === dragIdx) return;
    const next = [...order];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(target, 0, moved);
    setOrder(next);
    liveOrder.current = next;
    setDragIdx(target);
    startY.current = e.clientY;
  }

  function handlePointerUp() {
    if (dragIdx === null) return;
    setDragIdx(null);
    if (liveOrder.current.join() !== (usable || roleOrder).join()) persist(liveOrder.current);
  }

  // Move buttons, added at Session 78l alongside the drag rather than instead of
  // it, and this is a deliberate departure from D4's letter in service of D4's
  // reason.
  //
  // The gate chose drag over long-press on an AFFORDANCE argument: long-press is
  // invisible, undiscoverable, and easy to fire by accident with gloves on. Every
  // word of that indicts drag-only too. Dragging a 52px row on a phone with a
  // work glove is precisely the fine-motor gesture the argument was against, and
  // a person who cannot complete the drag has no other way to reorder their bar.
  // An arrow is the most discoverable control there is, it is a single tap, and
  // it costs one row of markup.
  //
  // It also closes an accessibility hole the gate did not consider: a pointer
  // drag is unreachable by keyboard, so a drag-only implementation would have
  // shipped a setting some people simply cannot use.
  function move(i, delta) {
    const target = i + delta;
    if (target < 0 || target >= order.length) return;
    const next = [...order];
    const [moved] = next.splice(i, 1);
    next.splice(target, 0, moved);
    setOrder(next);
    liveOrder.current = next;
    persist(next);
  }

  if (order.length < 2) return null;

  const split = Math.ceil(order.length / 2);
  const preview = [
    ...order.slice(0, split).map(k => BAR_SLOTS[k]?.label),
    'Scan',
    ...order.slice(split).map(k => BAR_SLOTS[k]?.label),
  ].filter(Boolean);

  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9A9A9A', marginBottom: 6 }}>
        Bottom bar on your phone
      </div>
      <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 12 }}>
        Drag a row, or use the arrows. Scan always holds the centre.
      </div>

      <div style={{ border: '1px solid #E8E8E8', borderRadius: 12, overflow: 'hidden', background: 'white', touchAction: 'none' }}>
        {order.map((key, i) => (
          <div
            key={key}
            onPointerDown={e => handlePointerDown(e, i)}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{
              height: ROW_H,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '0 14px',
              borderTop: i === 0 ? 'none' : '1px solid #F2F2F0',
              background: dragIdx === i ? '#F0FFF4' : 'white',
              cursor: 'grab',
              userSelect: 'none',
            }}
          >
            <IconGripVertical size={18} color="#C0C0C0" />
            <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: '#0F0F0F' }}>
              {BAR_SLOTS[key]?.label || key}
            </div>
            <div style={{ fontSize: 11, color: '#9A9A9A' }}>
              {i < split ? 'Left of Scan' : 'Right of Scan'}
            </div>
            <div style={{ display: 'flex', gap: 2 }}>
              <button
                onPointerDown={e => e.stopPropagation()}
                onClick={() => move(i, -1)}
                disabled={i === 0 || saving}
                aria-label={`Move ${BAR_SLOTS[key]?.label || key} earlier`}
                style={{
                  width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '1px solid #E8E8E8', borderRadius: 8, background: 'white',
                  cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? 0.3 : 1, padding: 0,
                }}
              >
                <IconChevronUp size={16} color="#5C5C5C" />
              </button>
              <button
                onPointerDown={e => e.stopPropagation()}
                onClick={() => move(i, 1)}
                disabled={i === order.length - 1 || saving}
                aria-label={`Move ${BAR_SLOTS[key]?.label || key} later`}
                style={{
                  width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '1px solid #E8E8E8', borderRadius: 8, background: 'white',
                  cursor: i === order.length - 1 ? 'default' : 'pointer',
                  opacity: i === order.length - 1 ? 0.3 : 1, padding: 0,
                }}
              >
                <IconChevronDown size={16} color="#5C5C5C" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 12, color: '#5C5C5C', marginTop: 10 }}>
        Your bar: {preview.join(' · ')}
      </div>

      {/* D5 stated where it is true. The cap is enforced in barForRole(), not
          here -- this screen cannot exceed it because it reorders a fixed set. */}
      <div style={{ fontSize: 11, color: '#9A9A9A', marginTop: 4 }}>
        {BAR_DESTINATION_CAP} destinations plus Scan. Which ones you get is set by your role.
      </div>

      {isCustom && (
        <button
          onClick={resetToRole}
          disabled={saving}
          style={{ marginTop: 10, background: 'none', border: 'none', padding: 0, fontSize: 12, fontWeight: 700, color: '#1D9E75', cursor: 'pointer' }}
        >
          Use my role's order
        </button>
      )}
      {error && <div style={{ fontSize: 12, color: '#A32D2D', marginTop: 8 }}>{error}</div>}
      {isMobile && <div style={{ height: 8 }} />}
    </div>
  );
}
